import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { auth, db, storage, ADMIN_EMAIL } from '../../firebase/config';
import {
  FaArrowLeft,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSpinner,
  FaImage,
  FaSave,
  FaTimes,
  FaUsers,
  FaGripVertical,
  FaGlobe,
  FaLinkedin,
  FaEnvelope,
  FaUserTie,
  FaCrown
} from 'react-icons/fa';
import './AdminTeam.css';

const ROLE_OPTIONS = [
  { value: 'founder', label: 'Founder', label_fr: 'Fondateur', icon: <FaCrown /> },
  { value: 'cofounder', label: 'Co-Founder', label_fr: 'Co-Fondateur', icon: <FaUserTie /> }
];

const AdminTeam = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [foundersList, setFoundersList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [thumbPreview, setThumbPreview] = useState(null);
  const [thumbFile, setThumbFile] = useState(null);
  const [activeTab, setActiveTab] = useState('en');
  const [formData, setFormData] = useState({
    // English content
    name: '',
    title_en: '',
    bio_en: '',
    // French content
    title_fr: '',
    bio_fr: '',
    // Common fields
    role: 'cofounder',
    imageUrl: '',
    videoThumbUrl: '',
    linkedin: '',
    email: '',
    order: 0
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser && currentUser.email.toLowerCase() === ADMIN_EMAIL.toLowerCase()) {
        setUser(currentUser);
        fetchFounders();
      } else {
        navigate('/admin');
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  const fetchFounders = async () => {
    try {
      const q = query(collection(db, 'founders'), orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      const items = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setFoundersList(items);
    } catch (err) {
      console.error('Error fetching founders:', err);
      setError('Failed to load founders');
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleThumbChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Thumbnail size must be less than 5MB');
        return;
      }
      setThumbFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async (file) => {
    try {
      const fileName = `founders/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snapshot.ref);
      return url;
    } catch (err) {
      console.error('Upload error:', err);
      if (err.code === 'storage/unauthorized') {
        throw new Error('Permission denied. Please update Firebase Storage rules.');
      }
      throw err;
    }
  };

  const deleteImage = async (imageUrl) => {
    if (imageUrl && imageUrl.includes('firebase')) {
      try {
        const imageRef = ref(storage, imageUrl);
        await deleteObject(imageRef);
      } catch (err) {
        console.log('Image deletion error (might not exist):', err);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      title_en: '',
      bio_en: '',
      title_fr: '',
      bio_fr: '',
      role: 'cofounder',
      imageUrl: '',
      videoThumbUrl: '',
      linkedin: '',
      email: '',
      order: foundersList.length
    });
    setImagePreview(null);
    setImageFile(null);
    setThumbPreview(null);
    setThumbFile(null);
    setEditingId(null);
    setShowForm(false);
    setActiveTab('en');
    setError('');
  };

  const handleEdit = (item) => {
    setFormData({
      name: item.name || '',
      title_en: item.title_en || '',
      bio_en: item.bio_en || '',
      title_fr: item.title_fr || '',
      bio_fr: item.bio_fr || '',
      role: item.role || 'cofounder',
      imageUrl: item.imageUrl || '',
      videoThumbUrl: item.videoThumbUrl || '',
      linkedin: item.linkedin || '',
      email: item.email || '',
      order: item.order || 0
    });
    setImagePreview(item.imageUrl || null);
    setThumbPreview(item.videoThumbUrl || null);
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.name.trim()) {
      setError('Name is required.');
      return;
    }

    if (!formData.title_en.trim()) {
      setError('English title is required.');
      setActiveTab('en');
      return;
    }

    setSaving(true);

    try {
      let imageUrl = formData.imageUrl;
      let videoThumbUrl = formData.videoThumbUrl;

      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
      }

      if (thumbFile) {
        videoThumbUrl = await uploadImage(thumbFile);
      }

      const founderData = {
        name: formData.name.trim(),
        // English content
        title_en: formData.title_en.trim(),
        bio_en: formData.bio_en.trim(),
        // French content
        title_fr: formData.title_fr.trim(),
        bio_fr: formData.bio_fr.trim(),
        // Common fields
        role: formData.role,
        imageUrl,
        videoThumbUrl,
        linkedin: formData.linkedin.trim(),
        email: formData.email.trim(),
        order: formData.order,
        updatedAt: new Date().toISOString()
      };

      if (editingId) {
        await updateDoc(doc(db, 'founders', editingId), founderData);
        setSuccess('Founder/Co-Founder updated successfully!');
      } else {
        founderData.createdAt = new Date().toISOString();
        await addDoc(collection(db, 'founders'), founderData);
        setSuccess('Founder/Co-Founder added successfully!');
      }

      await fetchFounders();
      resetForm();
    } catch (err) {
      console.error('Error saving founder:', err);
      setError(err.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.name}"?`)) {
      return;
    }

    try {
      if (item.imageUrl) {
        await deleteImage(item.imageUrl);
      }
      if (item.videoThumbUrl) {
        await deleteImage(item.videoThumbUrl);
      }
      await deleteDoc(doc(db, 'founders', item.id));
      setSuccess('Founder/Co-Founder deleted successfully!');
      await fetchFounders();
    } catch (err) {
      console.error('Error deleting founder:', err);
      setError('Failed to delete');
    }
  };

  const getRoleLabel = (role) => {
    const roleOption = ROLE_OPTIONS.find(r => r.value === role);
    return roleOption ? roleOption.label : role;
  };

  const getRoleIcon = (role) => {
    const roleOption = ROLE_OPTIONS.find(r => r.value === role);
    return roleOption ? roleOption.icon : <FaUserTie />;
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <FaSpinner className="spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="admin-team">
      <header className="admin-team-header">
        <button onClick={() => navigate('/admin/dashboard')} className="back-btn">
          <FaArrowLeft /> Back to Dashboard
        </button>
        <h1><FaUsers /> Team - Founders Management</h1>
      </header>

      <main className="admin-team-content">
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {!showForm ? (
          <>
            <div className="team-actions">
              <button onClick={() => setShowForm(true)} className="add-btn">
                <FaPlus /> Add Founder/Co-Founder
              </button>
            </div>

            <div className="founders-list">
              {foundersList.length === 0 ? (
                <div className="empty-state">
                  <FaUsers />
                  <h3>No founders added yet</h3>
                  <p>Click "Add Founder/Co-Founder" to add team leadership.</p>
                </div>
              ) : (
                foundersList.map((item, index) => (
                  <div key={item.id} className="founder-item">
                    <div className="founder-item-order">
                      <FaGripVertical />
                      <span>{index + 1}</span>
                    </div>
                    <div className="founder-item-image">
                      {item.imageUrl ? (
                        <img src={item.imageUrl} alt={item.name} />
                      ) : (
                        <div className="no-image"><FaImage /></div>
                      )}
                    </div>
                    <div className="founder-item-content">
                      <div className="founder-item-role">
                        {getRoleIcon(item.role)}
                        <span>{getRoleLabel(item.role)}</span>
                      </div>
                      <h3 className="founder-name">{item.name}</h3>
                      <div className="founder-item-langs">
                        <span className="lang-badge en">EN: {item.title_en || 'Not set'}</span>
                        <span className="lang-badge fr">FR: {item.title_fr || 'Not set'}</span>
                      </div>
                      <div className="founder-contact">
                        {item.linkedin && (
                          <a href={item.linkedin} target="_blank" rel="noopener noreferrer" className="contact-link">
                            <FaLinkedin />
                          </a>
                        )}
                        {item.email && (
                          <a href={`mailto:${item.email}`} className="contact-link">
                            <FaEnvelope />
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="founder-item-actions">
                      <button onClick={() => handleEdit(item)} className="edit-btn">
                        <FaEdit /> Edit
                      </button>
                      <button onClick={() => handleDelete(item)} className="delete-btn">
                        <FaTrash /> Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        ) : (
          <div className="team-form-container">
            <h2>{editingId ? 'Edit Founder/Co-Founder' : 'Add New Founder/Co-Founder'}</h2>

            {/* Language Tabs */}
            <div className="language-tabs">
              <button
                type="button"
                className={`lang-tab ${activeTab === 'en' ? 'active' : ''}`}
                onClick={() => setActiveTab('en')}
              >
                <FaGlobe /> English
              </button>
              <button
                type="button"
                className={`lang-tab ${activeTab === 'fr' ? 'active' : ''}`}
                onClick={() => setActiveTab('fr')}
              >
                <FaGlobe /> French
              </button>
            </div>

            <form onSubmit={handleSubmit} className="team-form">
              {/* Common Name Field (always visible) */}
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Jean-Christophe Lhotellier"
                />
              </div>

              {/* English Content */}
              <div className={`lang-content ${activeTab === 'en' ? 'active' : ''}`}>
                <div className="form-group">
                  <label>Title (English) *</label>
                  <input
                    type="text"
                    value={formData.title_en}
                    onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                    placeholder="e.g., Co-Founder & CEO"
                  />
                </div>

                <div className="form-group">
                  <label>Bio / Description (English)</label>
                  <textarea
                    value={formData.bio_en}
                    onChange={(e) => setFormData({ ...formData, bio_en: e.target.value })}
                    placeholder="Brief biography or description..."
                    rows={4}
                  />
                </div>
              </div>

              {/* French Content */}
              <div className={`lang-content ${activeTab === 'fr' ? 'active' : ''}`}>
                <div className="form-group">
                  <label>Title (French)</label>
                  <input
                    type="text"
                    value={formData.title_fr}
                    onChange={(e) => setFormData({ ...formData, title_fr: e.target.value })}
                    placeholder="e.g., Co-Fondateur & PDG"
                  />
                </div>

                <div className="form-group">
                  <label>Bio / Description (French)</label>
                  <textarea
                    value={formData.bio_fr}
                    onChange={(e) => setFormData({ ...formData, bio_fr: e.target.value })}
                    placeholder="Biographie ou description brève..."
                    rows={4}
                  />
                </div>
              </div>

              {/* Common Fields */}
              <div className="common-fields">
                <div className="form-group">
                  <label>Role Type *</label>
                  <div className="role-selector">
                    {ROLE_OPTIONS.map(role => (
                      <button
                        key={role.value}
                        type="button"
                        className={`role-option ${formData.role === role.value ? 'selected' : ''}`}
                        onClick={() => setFormData({ ...formData, role: role.value })}
                      >
                        {role.icon}
                        <span>{role.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label><FaLinkedin /> LinkedIn URL</label>
                    <input
                      type="text"
                      value={formData.linkedin}
                      onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                      placeholder="https://linkedin.com/in/username"
                    />
                  </div>
                  <div className="form-group">
                    <label><FaEnvelope /> Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="name@opteam-services.com"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label>Profile Photo</label>
                  <div className="image-upload-area">
                    {imagePreview ? (
                      <div className="image-preview">
                        <img src={imagePreview} alt="Preview" />
                        <button
                          type="button"
                          onClick={() => {
                            setImagePreview(null);
                            setImageFile(null);
                            setFormData({ ...formData, imageUrl: '' });
                          }}
                          className="remove-image-btn"
                        >
                          <FaTimes /> Remove
                        </button>
                      </div>
                    ) : (
                      <label className="image-upload-label">
                        <FaImage />
                        <span>Click to upload profile photo</span>
                        <small>Max size: 5MB (JPG, PNG, WebP)</small>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          hidden
                        />
                      </label>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Video Thumbnail Image</label>
                  <div className="image-upload-area">
                    {thumbPreview ? (
                      <div className="image-preview">
                        <img src={thumbPreview} alt="Video Thumbnail Preview" />
                        <button
                          type="button"
                          onClick={() => {
                            setThumbPreview(null);
                            setThumbFile(null);
                            setFormData({ ...formData, videoThumbUrl: '' });
                          }}
                          className="remove-image-btn"
                        >
                          <FaTimes /> Remove
                        </button>
                      </div>
                    ) : (
                      <label className="image-upload-label">
                        <FaImage />
                        <span>Click to upload video thumbnail</span>
                        <small>Max size: 5MB (JPG, PNG, WebP)</small>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleThumbChange}
                          hidden
                        />
                      </label>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={resetForm} className="cancel-btn">
                  <FaTimes /> Cancel
                </button>
                <button type="submit" disabled={saving} className="save-btn">
                  {saving ? (
                    <><FaSpinner className="spinner" /> Saving...</>
                  ) : (
                    <><FaSave /> {editingId ? 'Update' : 'Save'}</>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminTeam;
